import { gsap } from 'gsap';
import { Container, Sprite, Ticker } from '../../odie';
import { IAnimation } from '../../lib/pattern/IAnimation';

// types
//-----------------------------------------------------------------------------
export type CubeBeamAnimationOptions = {
  color: number;
  length: number;
  fromGlow: boolean;
  linearAdd?: boolean;
};

// constants
//-----------------------------------------------------------------------------
const manifest = {
  beam: 'fx.cube/fx.cube.beam.png',
  glow: 'fx.cube/fx.cube.glow.png',
};

/*
    cube effect beam animation
*/
export class CubeBeamAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  // input
  private _length: number;
  // scene
  private _beam!: Sprite;
  private _glowyFrom!: Sprite;
  private _glowyTo!: Sprite;
  // handlers
  private _stepper = (dt: number) => this._step(dt);
  private time = 0;
  private thickness = 1;

  // init
  //-------------------------------------------------------------------------
  constructor(options: CubeBeamAnimationOptions) {
    super();

    // set fields
    this._length = options.length;

    // spawn beam
    this._spawnBeam(options);

    // spawn from glowy
    if (options.fromGlow) {
      this._spawnGlowyFrom(options);
    }

    // create to glowy (will spawn later)
    this._createGlowyTo(options);
  }

  public static assets(): string[] {
    return [...Object.values(manifest)];
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(
    args: {
      duration?: number;
      ease?: string;
      thickness?: number;
    } = {},
  ): Promise<void> {
    this.thickness = args.thickness ?? 1;

    this.time = Math.random() * 1000;
    // start animation
    Ticker.system.add(this._stepper);

    // animate extend
    await this._animateExtend(args.duration, args.ease);

    // spawn to glowy
    this.addChild(this._glowyTo);
  }

  public stop() {
    // despawn to glowy
    this.removeChild(this._glowyTo);

    // stop animation
    Ticker.system.remove(this._stepper);
  }

  // private: stppers
  //-------------------------------------------------------------------------
  private _step(dt: number) {
    this.time += dt;

    if (this._beam) {
      const step = Math.sin(this.time * 0.3);
      this._beam.scaleY = step * 0.3 + 0.7 * this.thickness;
    }

    if (this._glowyTo) {
      const step = Math.cos(this.time * 0.3);
      this._glowyTo.scaleX = this._glowyTo.scaleY = step * 0.2 + 0.3;
    }
  }

  // private: scene
  //-------------------------------------------------------------------------
  private _spawnBeam(options: CubeBeamAnimationOptions) {
    this._beam = Sprite.from(manifest.beam);
    this._beam.anchorY = 0.5;
    if (!options.linearAdd) {
      this._beam.fill = options.color;
    } else {
      this._beam.linearAdd = options.color;
    }
    this._beam.scaleY = 0.5;
    this._beam.alpha = 0.8;
    this.addChild(this._beam);
  }

  private _spawnGlowyFrom(options: CubeBeamAnimationOptions) {
    this._glowyFrom = Sprite.from(manifest.glow);
    this.addChild(this._glowyFrom);
    this._glowyFrom.scaleX = this._glowyFrom.scaleY = 1.7;
    this._glowyFrom.anchorX = this._glowyFrom.anchorY = 0.5;
    this._glowyFrom.fill = options.color;
  }

  private _createGlowyTo(options: CubeBeamAnimationOptions) {
    this._glowyTo = Sprite.from(manifest.glow);
    // this.addChild(this._glowyTo);
    this._glowyTo.scaleX = this._glowyTo.scaleY = 0.3;
    this._glowyTo.anchorX = this._glowyTo.anchorY = 0.5;
    this._glowyTo.x = options.length;
    this._glowyTo.fill = options.color;
    this._glowyTo.alpha = 0.8;
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animateExtend(duration?: number, ease?: string) {
    // animation extend
    await gsap.to(this._beam, {
      width: this._length,
      duration: duration ?? 0.4,
      ease: ease ?? 'power1.in',
    });
  }
}
